import { useMemo } from "react";
import { arc as d3arc, pie as d3pie } from "d3-shape";
import { seriesColor, CHART_TOKENS, fmtInt } from "./theme";

// Donut chart with an optional centered statistic. Two modes:
//
//   - single-value progress (pass `value` + `total`): shows a filled arc
//     proportional to value/total with the remainder in a muted track.
//   - categorical breakdown (pass `data = [{label, value, color?}]`): shows
//     one arc per slice.
//
// The centered text defaults to the percentage for the progress mode or the
// total sum for categorical mode. Override with `centerValue` / `centerLabel`.
export const Donut = ({
  value,
  total,
  data,
  size = 180,
  thickness = 22,
  color,
  centerValue,
  centerLabel,
  valueFormatter = fmtInt,
}) => {
  const categorical = !!data;
  const radius = size / 2;
  const innerR = radius - thickness;

  const slices = useMemo(() => {
    if (categorical) {
      const pts = (data || []).filter((d) => d.value > 0);
      const total = pts.reduce((s, d) => s + d.value, 0) || 1;
      const gen = d3pie()
        .value((d) => d.value)
        .sort(null)
        .padAngle(0.01);
      return gen(pts).map((s, i) => ({
        d: d3arc().innerRadius(innerR).outerRadius(radius).cornerRadius(2)(s),
        color: pts[i].color || seriesColor(i),
        label: pts[i].label,
        value: pts[i].value,
        pct: pts[i].value / total,
      }));
    }
    const v = Math.max(0, Math.min(total || 0, value || 0));
    const pct = total ? v / total : 0;
    const full = d3arc().innerRadius(innerR).outerRadius(radius);
    return [
      {
        d: full({ startAngle: 0, endAngle: Math.PI * 2 }),
        color: CHART_TOKENS.grid,
        label: "remaining",
        value: (total || 0) - v,
        pct: 1 - pct,
      },
      {
        d: d3arc().innerRadius(innerR).outerRadius(radius).cornerRadius(3)({
          startAngle: 0,
          endAngle: Math.PI * 2 * pct,
        }),
        color: color || seriesColor(0),
        label: "done",
        value: v,
        pct,
      },
    ];
  }, [categorical, data, innerR, radius, value, total, color]);

  const centerTop = categorical
    ? (centerValue ?? valueFormatter(slices.reduce((s, x) => s + x.value, 0)))
    : (centerValue ?? `${Math.round((total ? value / total : 0) * 100)}%`);
  const centerBottom = centerLabel ?? (categorical ? "total" : "complete");

  return (
    <div style={{ position: "relative", width: size, height: size, display: "inline-block" }}>
      <svg width={size} height={size}>
        <g transform={`translate(${radius},${radius})`}>
          {slices.map((s, i) => (
            <path key={i} d={s.d} fill={s.color} />
          ))}
        </g>
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
          textAlign: "center",
        }}
      >
        <span
          style={{ fontSize: Math.max(16, size / 8), fontWeight: 700, color: CHART_TOKENS.labelStrong, lineHeight: 1 }}
        >
          {centerTop}
        </span>
        <span style={{ fontSize: "var(--font-size-caption)", color: CHART_TOKENS.label, marginTop: 4 }}>
          {centerBottom}
        </span>
      </div>
    </div>
  );
};
